import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BookOpen,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Printer,
  Save,
  Users,
  UserMinus,
  UserCheck,
} from "lucide-react";
import { ExcelExportLabel, excelExportButtonA11y } from "@/components/excelExportLabel";
import { CellMeetingCalendarPanel } from "@/components/CellMeetingCalendarPanel";
import { formatMeetingDateDisplay } from "@/lib/cellMeetingSchedule";
import { getCellMongoId } from "@/lib/cellIds";
import {
  COL_ASISTIO,
  COL_CARGO,
  COL_DIRIGE,
  COL_MOTIVO_AUSENCIA,
  COL_NOMBRE,
  COL_OBSERVACION,
  DISC_REPORT_BY_MOTHER_COLUMNS,
  DISC_REPORT_DETAIL_COLUMNS,
  ReportColumnHeader,
  safeColumnLabel,
} from "@/lib/reportColumnLabels";

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const NativeSelect = ({ value, onChange, placeholder, children, className = "", disabled = false, id }) => (
  <select
    id={id}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className={`flex h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
  >
    {placeholder ? <option value="">{placeholder}</option> : null}
    {children}
  </select>
);

const REP_TYPE_LABELS = {
  absent: "Ausentes al discipulado",
  present: "Asistentes al discipulado",
  by_mother: "Historial por célula madre",
  by_leader: "Historial por líder",
  stats: "Porcentaje / totales (rango)",
};

function discReportCellText(row, key) {
  if (!row || typeof row !== "object") return "—";
  if (key === "attended") return row.attended ? "Sí" : "No";
  const v = row[key];
  if (v == null || v === "") return "—";
  return String(v);
}

function DiscReportDataTable({ columns, rows, minWidth = "520px" }) {
  const cols = Array.isArray(columns) && columns.length > 0 ? columns : null;
  if (!cols) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        No se pudo mostrar la tabla del informe: configuración de columnas no disponible.
      </p>
    );
  }
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <div className="overflow-x-auto rounded border print:overflow-visible print:border-gray-400">
      <table className={`w-full text-sm print:text-xs`} style={{ minWidth }}>
        <thead className="bg-gray-100">
          <tr>
            {cols.map((col) => (
              <th
                key={col.key}
                className={`px-2 py-2 ${col.align === "center" ? "text-center" : "text-left"}`}
              >
                <ReportColumnHeader>{safeColumnLabel(col.label, col.key)}</ReportColumnHeader>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {safeRows.map((row, index) => (
            <tr key={`${row?.meeting_date ?? "d"}-${row?.parent_cell_id ?? "p"}-${index}`} className="border-t">
              {cols.map((col) => (
                <td
                  key={col.key}
                  className={`px-2 py-1 ${col.align === "center" ? "text-center" : ""} ${
                    col.key === "meeting_date" ? "whitespace-nowrap" : ""
                  }`}
                >
                  {discReportCellText(row, col.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function downloadDiscBlobExport(api, path, params, fallbackName, onError) {
  try {
    const res = await api.get(path, { params, responseType: "blob" });
    const mime = res.headers["content-type"] || "application/octet-stream";
    const blob = new Blob([res.data], { type: mime });
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const filename = (m && m[1] ? m[1].trim() : null) || fallbackName;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let mapped = err;
    if (err.response?.data instanceof Blob) {
      try {
        const t = await err.response.data.text();
        const j = JSON.parse(t);
        mapped = { ...err, response: { ...err.response, data: j } };
      } catch {
        /* keep */
      }
    }
    onError?.(mapped);
  }
}

async function downloadDiscExport(api, kind, parentCellId, meetingDate) {
  const path =
    kind === "excel"
      ? "/cell-discipleship-attendance/export/excel"
      : "/cell-discipleship-attendance/export/pdf";
  const slug = String(parentCellId || "").slice(0, 12);
  const fallback = `discipulado_${slug}_${meetingDate}.${kind === "excel" ? "xlsx" : "pdf"}`;
  await downloadDiscBlobExport(
    api,
    path,
    { parent_cell_id: parentCellId, meeting_date: meetingDate },
    fallback
  );
}

async function downloadDiscReportExport(api, kind, params, onError) {
  const path =
    kind === "excel"
      ? "/cell-discipleship-attendance/report/export/excel"
      : "/cell-discipleship-attendance/report/export/pdf";
  const rt = params.report_type || "informe";
  const md = params.meeting_date;
  const df = params.date_from || "inicio";
  const dt = params.date_to || "fin";
  const fallback = md
    ? `informe_discipulado_${rt}_${md}.${kind === "excel" ? "xlsx" : "pdf"}`
    : `informe_discipulado_${rt}_${df}_${dt}.${kind === "excel" ? "xlsx" : "pdf"}`;
  await downloadDiscBlobExport(api, path, params, fallback, onError);
}

function StatChip({ icon: Icon, label, value, className }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm ${className}`}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
      <div className="min-w-0 leading-tight">
        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-600">{label}</div>
        <div className="text-lg font-bold tabular-nums text-gray-900">{value}</div>
      </div>
    </div>
  );
}

function participantRowKey(r, index) {
  const k = r?.row_key;
  if (k != null && String(k).trim() !== "") return String(k);
  return `row-${r?.attendee_member_id ?? "x"}-${r?.role ?? "r"}-${r?.child_cell_id ?? "c"}-${index}`;
}

/** Tabla con estructura fija en tbody (evita insertBefore al cambiar carga / filas). */
function ParticipantsTable({ rows, loading, onUpdateRow }) {
  const safeRows = Array.isArray(rows) ? rows : [];

  let bodyRow = null;
  if (loading) {
    bodyRow = (
      <tr key="disc-tbody-loading">
        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" aria-hidden />
            Cargando convocatoria…
          </span>
        </td>
      </tr>
    );
  } else if (safeRows.length === 0) {
    bodyRow = (
      <tr key="disc-tbody-empty">
        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
          Sin convocatoria (sin líderes asignados en esta célula madre).
        </td>
      </tr>
    );
  } else {
    bodyRow = safeRows.map((r, index) => {
      const rk = participantRowKey(r, index);
      return (
        <tr key={rk} className="border-b border-gray-100 odd:bg-white even:bg-indigo-50/40">
          <td className="px-3 py-2 font-medium text-gray-900">{r.attendee_name ?? "—"}</td>
          <td className="px-3 py-2 text-gray-700">{r.role_label_es ?? "—"}</td>
          <td className="px-3 py-2 text-gray-700">{r.directs_cell_name ?? "—"}</td>
          <td className="px-3 py-2">
            <label className="inline-flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={!!r.attended}
                onChange={(e) => onUpdateRow(rk, { attended: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="text-xs text-gray-600">{r.attended ? "Sí" : "No"}</span>
            </label>
          </td>
          <td className="px-3 py-2">
            <Input
              className="h-8 text-xs"
              value={r.absence_reason ?? ""}
              onChange={(e) => onUpdateRow(rk, { absence_reason: e.target.value })}
              placeholder="—"
            />
          </td>
          <td className="px-3 py-2">
            <Input
              className="h-8 text-xs"
              value={r.observation ?? ""}
              onChange={(e) => onUpdateRow(rk, { observation: e.target.value })}
              placeholder="—"
            />
          </td>
        </tr>
      );
    });
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-indigo-900 text-white print:bg-gray-800">
          <tr>
            <th className="px-3 py-2 font-medium">
              <ReportColumnHeader>{COL_NOMBRE}</ReportColumnHeader>
            </th>
            <th className="px-3 py-2 font-medium">
              <ReportColumnHeader>{COL_CARGO}</ReportColumnHeader>
            </th>
            <th className="px-3 py-2 font-medium">
              <ReportColumnHeader>{COL_DIRIGE}</ReportColumnHeader>
            </th>
            <th className="px-3 py-2 font-medium">
              <ReportColumnHeader>{COL_ASISTIO}</ReportColumnHeader>
            </th>
            <th className="px-3 py-2 font-medium">
              <ReportColumnHeader>{COL_MOTIVO_AUSENCIA}</ReportColumnHeader>
            </th>
            <th className="px-3 py-2 font-medium">
              <ReportColumnHeader>{COL_OBSERVACION}</ReportColumnHeader>
            </th>
          </tr>
        </thead>
        <tbody>{bodyRow}</tbody>
      </table>
    </div>
  );
}

function SummaryBar({ summary, loading }) {
  const dash = "—";
  const s = summary && typeof summary === "object" ? summary : null;
  return (
    <div className="flex flex-wrap items-stretch gap-2 print:gap-3" aria-busy={loading}>
      <StatChip
        icon={Users}
        label="Total convocados"
        value={loading ? dash : (s?.total_convocados ?? 0)}
        className="min-w-[130px] flex-1 border-indigo-100 bg-indigo-50/90"
      />
      <StatChip
        icon={UserCheck}
        label="Asistentes"
        value={loading ? dash : (s?.total_asistentes ?? 0)}
        className="min-w-[120px] flex-1 border-emerald-100 bg-emerald-50/90"
      />
      <StatChip
        icon={UserMinus}
        label="Ausentes"
        value={loading ? dash : (s?.total_ausentes ?? 0)}
        className="min-w-[120px] flex-1 border-amber-100 bg-amber-50/90"
      />
      <StatChip
        icon={BookOpen}
        label="% Asistencia"
        value={loading ? dash : `${s?.porcentaje_asistencia ?? 0}%`}
        className="min-w-[120px] flex-1 border-violet-100 bg-violet-50/90"
      />
      <StatChip
        icon={Users}
        label="Células hijas"
        value={loading ? dash : (s?.total_celulas_hijas_representadas ?? 0)}
        className="min-w-[130px] flex-1 border-sky-100 bg-sky-50/90"
      />
    </div>
  );
}

/**
 * Asistencia de discipulado de líderes (célula madre + hijas).
 */
export function CellDiscipleshipAttendance({ api, cells = [], members = [], formatApiError }) {
  const [subTab, setSubTab] = useState("form");
  const [motherId, setMotherId] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [parentName, setParentName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [repExporting, setRepExporting] = useState(null);
  const formAnchorRef = useRef(null);

  const [repType, setRepType] = useState("absent");
  const [repMeetingDate, setRepMeetingDate] = useState(todayIso);
  const [repMotherId, setRepMotherId] = useState("");
  const [repDateFrom, setRepDateFrom] = useState(todayIso);
  const [repDateTo, setRepDateTo] = useState(todayIso);
  const [repLeaderId, setRepLeaderId] = useState("");
  const [repLoading, setRepLoading] = useState(false);
  const [repData, setRepData] = useState(null);
  const [repMessage, setRepMessage] = useState(null);

  const [histRows, setHistRows] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const participantsSeq = useRef(0);
  const historySeq = useRef(0);

  const cellOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of cells) {
      const cid = String(c?.id || c?._id || "").trim();
      if (!cid || seen.has(cid)) continue;
      seen.add(cid);
      out.push({ cid, label: c?.name || "Sin nombre" });
    }
    return out;
  }, [cells]);

  const selectedMother = useMemo(
    () => cells.find((c) => getCellMongoId(c) === String(motherId || "").trim()) || null,
    [cells, motherId],
  );

  const leaderName = useMemo(() => {
    if (!selectedMother?.leader_id) return "Sin asignar";
    const m = members.find((x) => String(x.id || x._id) === String(selectedMother.leader_id));
    return m?.name || "Sin asignar";
  }, [selectedMother, members]);

  const calendarEntries = useMemo(
    () =>
      (histRows || [])
        .filter((h) => !motherId || String(h.parent_cell_id || "") === String(motherId))
        .map((h) => ({
          meeting_date: h.meeting_date,
          scheduled_meeting_date: h.meeting_date,
          meeting_suspended: false,
        })),
    [histRows, motherId],
  );

  const resetParticipantView = useCallback(() => {
    setRows([]);
    setSummary(null);
    setParentName("");
  }, []);

  const loadHistory = useCallback(async () => {
    const seq = ++historySeq.current;
    setHistLoading(true);
    try {
      const res = await api.get("/cell-discipleship-attendance/history", {
        params: { limit: 150, parent_cell_id: motherId || undefined },
      });
      if (seq !== historySeq.current) return;
      setHistRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (seq !== historySeq.current) return;
      setHistRows([]);
    } finally {
      if (seq === historySeq.current) setHistLoading(false);
    }
  }, [api, motherId]);

  const loadParticipants = useCallback(async () => {
    const pid = String(motherId || "").trim();
    const md = String(meetingDate || "").trim();
    if (!pid || !md) {
      resetParticipantView();
      setLoading(false);
      return;
    }

    const seq = ++participantsSeq.current;
    setLoading(true);
    setMessage(null);
    resetParticipantView();

    try {
      const res = await api.get("/cell-discipleship-attendance/participants", {
        params: { parent_cell_id: pid, meeting_date: md },
      });
      if (seq !== participantsSeq.current) return;
      const data = res.data ?? {};
      setParentName(data.parent_cell_name || "");
      setSummary(data.summary && typeof data.summary === "object" ? data.summary : null);
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      if (seq !== participantsSeq.current) return;
      setMessage({ type: "err", text: formatApiError(err) });
      resetParticipantView();
    } finally {
      if (seq === participantsSeq.current) setLoading(false);
    }
  }, [api, motherId, meetingDate, formatApiError, resetParticipantView]);

  useEffect(() => {
    void loadParticipants();
  }, [loadParticipants]);

  const handleMotherChange = (nextId) => {
    setMotherId(nextId);
    setMeetingDate("");
    setMessage(null);
    resetParticipantView();
  };

  const handleMeetingDateChange = (nextDate) => {
    setMeetingDate(nextDate);
    setMessage(null);
    resetParticipantView();
    setTimeout(() => {
      formAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const updateRow = useCallback((rowKey, patch) => {
    setRows((prev) =>
      prev.map((r, index) => {
        const rk = participantRowKey(r, index);
        if (rk === rowKey || r?.row_key === rowKey) return { ...r, ...patch };
        return r;
      })
    );
  }, []);

  const handleSave = async () => {
    if (!motherId || !meetingDate) {
      setMessage({ type: "err", text: "Seleccione célula madre y fecha." });
      return;
    }
    if (!rows.length) {
      setMessage({ type: "err", text: "No hay convocatoria para guardar." });
      return;
    }
    const items = rows.map((r) => ({
      role: r.role,
      child_cell_id: r.child_cell_id || null,
      attendee_member_id: r.attendee_member_id,
      attended: !!r.attended,
      absence_reason: (r.absence_reason || "").trim() || null,
      observation: (r.observation || "").trim() || null,
    }));
    setSaving(true);
    setMessage(null);
    try {
      await api.post("/cell-discipleship-attendance", {
        parent_cell_id: motherId,
        meeting_date: meetingDate,
        items,
      });
      setMessage({ type: "ok", text: "Asistencia de discipulado guardada." });
      await loadParticipants();
      await loadHistory();
    } catch (err) {
      setMessage({ type: "err", text: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (kind) => {
    if (!motherId || !meetingDate) {
      setMessage({ type: "err", text: "Seleccione célula madre y fecha para exportar." });
      return;
    }
    setExporting(kind);
    setMessage(null);
    try {
      await downloadDiscExport(api, kind, motherId, meetingDate);
    } catch (err) {
      setMessage({ type: "err", text: formatApiError(err) });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    if (subTab === "form" && motherId && !loading) {
      void loadHistory();
    }
  }, [subTab, motherId, loading, loadHistory]);

  const buildDiscReportParams = useCallback(() => {
    const params = { report_type: repType };
    const mother = String(repMotherId || "").trim();
    const leader = String(repLeaderId || "").trim();
    if (repType === "absent" || repType === "present") {
      if (repMeetingDate) params.meeting_date = repMeetingDate;
      if (mother) params.parent_cell_id = mother;
    } else if (repType === "stats" || repType === "by_mother" || repType === "by_leader") {
      if (repDateFrom) params.date_from = repDateFrom;
      if (repDateTo) params.date_to = repDateTo;
      if (mother) params.parent_cell_id = mother;
      if (repType === "by_leader" && leader) params.leader_member_id = leader;
    }
    return params;
  }, [repType, repMeetingDate, repMotherId, repDateFrom, repDateTo, repLeaderId]);

  const runReport = async () => {
    setRepLoading(true);
    setRepMessage(null);
    setRepData(null);
    try {
      const res = await api.get("/cell-discipleship-attendance/report", { params: buildDiscReportParams() });
      setRepData(res.data ?? null);
    } catch (err) {
      setRepMessage({ type: "err", text: formatApiError(err) });
    } finally {
      setRepLoading(false);
    }
  };

  const hasRepExportable = useMemo(
    () =>
      Boolean(
        repData &&
          (repData.stats ||
            (Array.isArray(repData.rows) && repData.rows.length > 0) ||
            (Array.isArray(repData.by_mother) && repData.by_mother.length > 0))
      ),
    [repData]
  );

  const handleReportExport = async (kind) => {
    if (!hasRepExportable) {
      setRepMessage({ type: "err", text: "Genere un informe con resultados antes de exportar." });
      return;
    }
    setRepExporting(kind);
    setRepMessage(null);
    try {
      await downloadDiscReportExport(api, kind, buildDiscReportParams(), (err) => {
        throw err;
      });
    } catch (err) {
      setRepMessage({ type: "err", text: formatApiError(err) });
    } finally {
      setRepExporting(null);
    }
  };

  const handleReportPrint = () => {
    if (!hasRepExportable) {
      setRepMessage({ type: "err", text: "Genere un informe con resultados antes de imprimir." });
      return;
    }
    const el = document.getElementById("disc-report-print");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => window.print(), 300);
  };

  const repMotherLabel = useMemo(() => {
    if (!repMotherId) return "Todas las células madre";
    const hit = cellOptions.find((o) => o.cid === repMotherId);
    return hit?.label || repMotherId;
  }, [repMotherId, cellOptions]);

  const attendedCount = useMemo(() => (Array.isArray(rows) ? rows.filter((r) => r.attended).length : 0), [rows]);
  const showFormPanel = subTab === "form";
  const showReportsPanel = subTab === "reports";
  const hasMother = Boolean(String(motherId || "").trim());

  return (
    <div className="space-y-6 print:space-y-4">
      <div className="print:hidden">
        <h3 className="text-lg font-semibold text-indigo-950">Asistencia de Discipulado</h3>
        <p className="mt-1 text-sm text-gray-600">
          Registro pastoral de líderes y co-líderes convocados por la célula madre (incluye células hijas por{" "}
          <code className="rounded bg-gray-100 px-1">parent_cell_id</code>). No sustituye la hoja de asistencia
          regular de célula.
        </p>
      </div>

      <div className="print:hidden flex flex-wrap gap-1 rounded-lg bg-indigo-50 p-1" role="tablist" aria-label="Sección discipulado">
        <button
          type="button"
          role="tab"
          aria-selected={showFormPanel}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            showFormPanel ? "bg-white text-indigo-800 shadow-sm" : "text-indigo-900/80 hover:bg-white/60"
          }`}
          onClick={() => setSubTab("form")}
        >
          Formulario e historial
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={showReportsPanel}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            showReportsPanel ? "bg-white text-indigo-800 shadow-sm" : "text-indigo-900/80 hover:bg-white/60"
          }`}
          onClick={() => setSubTab("reports")}
        >
          Reportes
        </button>
      </div>

      <div className={showFormPanel ? "mt-0 block space-y-6 outline-none" : "hidden"} role="tabpanel" aria-hidden={!showFormPanel}>
          {message ? (
            <div
              className={`print:hidden rounded-lg border px-4 py-3 text-sm ${
                message.type === "ok"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
            >
              {message.text}
            </div>
          ) : null}

          <Card className="border-indigo-100 shadow-sm print:border-gray-300">
            <CardHeader className="pb-3 print:pb-2">
              <CardTitle className="text-lg text-indigo-950">Convocatoria</CardTitle>
              <CardDescription>
                Elija la célula madre. El calendario usa el día habitual configurado en la célula.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="disc-mother">Célula madre</Label>
                <NativeSelect
                  id="disc-mother"
                  value={motherId}
                  onChange={handleMotherChange}
                  placeholder="Seleccionar célula madre"
                >
                  {cellOptions.map(({ cid, label }) => (
                    <option key={cid} value={cid}>
                      {label}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </CardContent>
          </Card>

          {selectedMother ? (
            <CellMeetingCalendarPanel
              cell={selectedMother}
              leaderName={leaderName}
              entries={calendarEntries}
              loading={histLoading}
              selectedDate={meetingDate}
              reportNoun="reporte"
              onEnterReport={(actual) => handleMeetingDateChange(actual)}
              onViewReport={(actual) => handleMeetingDateChange(actual)}
            />
          ) : null}

          {hasMother && meetingDate ? (
            <div ref={formAnchorRef} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 print:hidden">
              Fecha de discipulado seleccionada:{" "}
              <span className="font-semibold">{formatMeetingDateDisplay(meetingDate)}</span>
            </div>
          ) : null}

          {hasMother && meetingDate ? <SummaryBar summary={summary} loading={loading} /> : null}

          <div className="print:hidden flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => void loadParticipants()} disabled={loading || !hasMother}>
              <span className="inline-flex items-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                <span>{loading ? "Cargando…" : "Recargar"}</span>
              </span>
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || loading || !rows.length} className="gap-1">
              <Save className="h-4 w-4" />
              {saving ? "Guardando…" : "Guardar asistencia"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="gap-1"
              disabled={!hasMother || !meetingDate || exporting === "excel"}
              onClick={() => handleExport("excel")}
              {...excelExportButtonA11y}
            >
              <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
              <ExcelExportLabel busy={exporting === "excel"} />
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="gap-1"
              disabled={!hasMother || !meetingDate || exporting === "pdf"}
              onClick={() => handleExport("pdf")}
            >
              <FileText className="h-4 w-4" />
              PDF
            </Button>
            <Button type="button" variant="outline" className="gap-1" onClick={() => window.print()}>
              <Printer className="h-4 w-4" />
              Imprimir
            </Button>
          </div>

          <Card
            id="disc-print-area"
            className={`overflow-hidden border-gray-200 shadow-sm ${hasMother ? "" : "hidden"}`}
            aria-hidden={!hasMother}
          >
            <CardHeader className="border-b bg-gray-50/80 py-3 print:bg-white">
              <CardTitle className="text-base">{parentName || "Discipulado"}</CardTitle>
              <CardDescription className="text-xs">
                Fecha: {meetingDate} · {attendedCount} asistentes de {rows.length}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ParticipantsTable rows={rows} loading={loading} onUpdateRow={updateRow} />
            </CardContent>
          </Card>

          <Card className="border-gray-200 print:hidden">
            <CardHeader>
              <CardTitle className="text-base">Historial de reuniones (vista rápida)</CardTitle>
              <CardDescription>
                Últimas reuniones registradas{hasMother ? " para la célula madre seleccionada" : " (todas las accesibles)"}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {histLoading ? (
                <p className="text-sm text-gray-500">Cargando historial…</p>
              ) : !histRows.length ? (
                <p className="text-sm text-gray-500">Sin registros aún.</p>
              ) : (
                <ul className="max-h-56 space-y-2 overflow-y-auto text-sm">
                  {histRows.map((h, index) => {
                    const hk = `${h?.parent_cell_id ?? "p"}-${h?.meeting_date ?? "d"}-${index}`;
                    return (
                      <li
                        key={hk}
                        className="flex flex-wrap justify-between gap-2 rounded border border-gray-100 bg-gray-50 px-3 py-2"
                      >
                        <span className="font-medium text-gray-800">{h.parent_cell_name ?? "—"}</span>
                        <span className="text-gray-600">{h.meeting_date ?? "—"}</span>
                        <span className="text-indigo-700">
                          {h.total_asistentes ?? 0}/{h.total_convocados ?? 0} ({h.porcentaje_asistencia ?? 0}%)
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
      </div>

      <div
        className={showReportsPanel ? "mt-0 block space-y-4 outline-none" : "hidden"}
        role="tabpanel"
        aria-hidden={!showReportsPanel}
      >
          <Card className="print:hidden border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Reportes de discipulado</CardTitle>
              <CardDescription>
                Ausentes, asistentes, historial por célula madre, por líder y porcentajes globales en el rango de fechas.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {repMessage ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {repMessage.text}
                </div>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>Tipo de reporte</Label>
                  <NativeSelect value={repType} onChange={setRepType} placeholder="">
                    <option value="absent">Ausentes al discipulado</option>
                    <option value="present">Asistentes al discipulado</option>
                    <option value="by_mother">Historial por célula madre</option>
                    <option value="by_leader">Historial por líder (ID miembro)</option>
                    <option value="stats">Porcentaje / totales (rango)</option>
                  </NativeSelect>
                </div>
                <div key={`rep-extra-${repType}`} className="contents">
                  {(repType === "absent" || repType === "present") ? (
                    <div className="space-y-2">
                      <Label>Fecha reunión</Label>
                      <Input type="date" value={repMeetingDate} onChange={(e) => setRepMeetingDate(e.target.value)} />
                    </div>
                  ) : null}
                  {(repType === "stats" || repType === "by_mother" || repType === "by_leader") ? (
                    <div className="space-y-2">
                      <Label>Desde</Label>
                      <Input type="date" value={repDateFrom} onChange={(e) => setRepDateFrom(e.target.value)} />
                    </div>
                  ) : null}
                  {(repType === "stats" || repType === "by_mother" || repType === "by_leader") ? (
                    <div className="space-y-2">
                      <Label>Hasta</Label>
                      <Input type="date" value={repDateTo} onChange={(e) => setRepDateTo(e.target.value)} />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label>Célula madre (opcional)</Label>
                  <NativeSelect value={repMotherId} onChange={setRepMotherId} placeholder="Todas">
                    <option value="">Todas</option>
                    {cellOptions.map(({ cid, label }) => (
                      <option key={`rep-${cid}`} value={cid}>
                        {label}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                {repType === "by_leader" ? (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>ID miembro (líder / co-líder)</Label>
                    <Input
                      value={repLeaderId}
                      onChange={(e) => setRepLeaderId(e.target.value)}
                      placeholder="ObjectId del miembro"
                    />
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <Button type="button" onClick={runReport} disabled={repLoading} className="gap-2 w-full sm:w-auto">
                  <span className="inline-flex items-center gap-2">
                    {repLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" />}
                    <span>Generar reporte</span>
                  </span>
                </Button>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1 flex-1 sm:flex-none"
                    disabled={!hasRepExportable || !!repExporting}
                    onClick={() => handleReportExport("excel")}
                    {...excelExportButtonA11y}
                  >
                    <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
                    <ExcelExportLabel busy={repExporting === "excel"} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1 flex-1 sm:flex-none"
                    disabled={!hasRepExportable || !!repExporting}
                    onClick={() => handleReportExport("pdf")}
                  >
                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    {repExporting === "pdf" ? "Generando…" : "PDF"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-1 flex-1 sm:flex-none"
                    disabled={!hasRepExportable}
                    onClick={handleReportPrint}
                  >
                    <Printer className="h-4 w-4 shrink-0" aria-hidden />
                    Imprimir
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div id="disc-report-print" className="space-y-4">
              {repData ? (
                <p className="text-sm text-gray-600 print:text-black">
                  <span className="font-medium text-gray-900">{REP_TYPE_LABELS[repType] || repType}</span>
                  {" · "}
                  {repMotherLabel}
                  {repType === "absent" || repType === "present" ? ` · Fecha ${repMeetingDate}` : null}
                  {repType === "stats" || repType === "by_mother" || repType === "by_leader"
                    ? ` · ${repDateFrom} a ${repDateTo}`
                    : null}
                </p>
              ) : null}

              {repData?.stats ? (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <StatChip icon={Users} label="Reuniones" value={repData.stats.total_reuniones ?? 0} className="border-gray-200 bg-white" />
                  <StatChip icon={Users} label="Convocados (suma)" value={repData.stats.total_convocados_sum ?? 0} className="border-gray-200 bg-white" />
                  <StatChip icon={UserCheck} label="Asistentes (suma)" value={repData.stats.total_asistentes_sum ?? 0} className="border-gray-200 bg-white" />
                  <StatChip icon={BookOpen} label="% global" value={`${repData.stats.porcentaje_asistencia_global ?? 0}%`} className="border-gray-200 bg-white" />
                </div>
              ) : null}

              {repType === "by_mother" && repData && Array.isArray(repData.by_mother) && repData.by_mother.length > 0 ? (
                <DiscReportDataTable
                  columns={DISC_REPORT_BY_MOTHER_COLUMNS}
                  rows={repData.by_mother}
                  minWidth="520px"
                />
              ) : null}

              {(repType === "absent" || repType === "present" || repType === "by_leader") &&
              repData &&
              Array.isArray(repData.rows) &&
              repData.rows.length > 0 ? (
                <DiscReportDataTable
                  columns={DISC_REPORT_DETAIL_COLUMNS}
                  rows={repData.rows}
                  minWidth="760px"
                />
              ) : null}

              {repData && !repData.rows?.length && !repData.by_mother?.length && !repData.stats ? (
                <p className="text-sm text-gray-500">Sin resultados para los filtros elegidos.</p>
              ) : null}
          </div>
      </div>
    </div>
  );
}
